import { useEffect, useMemo, useState } from 'react'
import api from '../services/api'
import { GROUP_PLAY_SLOTS, type GroupPlaySlot } from '../constants/groupPlay'
import {
  getLeagueTeamDisplayName,
  LEAGUE_TEAM_NAMES,
  type LeagueTeamName,
} from '../constants/teamNames'
import {
  fetchCurrentUser,
  isAdminUser,
  type AuthenticatedUser,
} from '../services/auth'
import { updateUserAssignments } from '../services/users'
import './Admin.css'

interface GameSummary {
  id: number
  homeTeam: string
  awayTeam: string
  homeScore: number
  awayScore: number
  playedAt: string | null
  uploadedAt: string
  player1User?: {
    id: number
    username: string
    displayName: string
    teamAbbreviation: string
  }
  player2User?: {
    id: number
    username: string
    displayName: string
    teamAbbreviation: string
  }
}

interface OutstandingGroupPlayMatchup {
  homeSlot: string
  awaySlot: string
  homeUser: {
    id: number
    username: string
    displayName: string
  }
  awayUser: {
    id: number
    username: string
    displayName: string
  }
}

interface AdminUserSummary {
  id: number
  username: string
  discordUsername?: string | null
  discordGlobalName?: string | null
  discordAvatar?: string | null
  leagueTeamName?: string | null
  groupPlaySlot?: string | null
  lastLoginAt: string | null
  createdAt: string
}

interface TeamProphetBonus {
  teamName: LeagueTeamName
  bonusPoints: number
}

type TeamSelectionValue = LeagueTeamName | ''
type GroupPlaySelectionValue = GroupPlaySlot | ''
type ForfeitOutcome = 'home_win' | 'away_win' | 'double_loss'

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Unknown'
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) {
    return 'Unknown'
  }
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function createTeamSelectionMap(users: AdminUserSummary[]): Record<number, TeamSelectionValue> {
  return Object.fromEntries(users.map(user => [user.id, (user.leagueTeamName ?? '') as TeamSelectionValue]))
}

function createGroupPlaySelectionMap(users: AdminUserSummary[]): Record<number, GroupPlaySelectionValue> {
  return Object.fromEntries(users.map(user => [user.id, (user.groupPlaySlot ?? '') as GroupPlaySelectionValue]))
}

function createProphetBonusSelectionMap(bonuses: TeamProphetBonus[]): Record<LeagueTeamName, number> {
  return Object.fromEntries(
    LEAGUE_TEAM_NAMES.map(teamName => [
      teamName,
      bonuses.find(bonus => bonus.teamName === teamName)?.bonusPoints ?? 0,
    ])
  ) as Record<LeagueTeamName, number>
}

export default function Admin() {
  const [currentUser, setCurrentUser] = useState<AuthenticatedUser | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [users, setUsers] = useState<AdminUserSummary[]>([])
  const [usersLoading, setUsersLoading] = useState(true)
  const [teamSelections, setTeamSelections] = useState<Record<number, TeamSelectionValue>>({})
  const [groupPlaySelections, setGroupPlaySelections] = useState<Record<number, GroupPlaySelectionValue>>({})
  const [prophetBonuses, setProphetBonuses] = useState<TeamProphetBonus[]>([])
  const [prophetBonusSelections, setProphetBonusSelections] = useState<Record<LeagueTeamName, number>>(
    createProphetBonusSelectionMap([])
  )
  const [games, setGames] = useState<GameSummary[]>([])
  const [outstandingMatchups, setOutstandingMatchups] = useState<OutstandingGroupPlayMatchup[]>([])
  const [gamesLoading, setGamesLoading] = useState(true)
  const [selectedGameIds, setSelectedGameIds] = useState<number[]>([])
  const [forfeitHomeUserId, setForfeitHomeUserId] = useState('')
  const [forfeitAwayUserId, setForfeitAwayUserId] = useState('')
  const [forfeitOutcome, setForfeitOutcome] = useState<ForfeitOutcome>('double_loss')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [assigningForfeit, setAssigningForfeit] = useState(false)
  const [savingUserId, setSavingUserId] = useState<number | null>(null)
  const [savingProphetBonusTeam, setSavingProphetBonusTeam] = useState<LeagueTeamName | null>(null)

  useEffect(() => {
    const loadUser = async () => {
      try {
        const user = await fetchCurrentUser()
        setCurrentUser(user)
      } catch {
        setCurrentUser(null)
      } finally {
        setAuthLoading(false)
      }
    }

    loadUser()
  }, [])

  useEffect(() => {
    const loadAdminData = async () => {
      try {
        const [
          gamesResponse,
          usersResponse,
          prophetBonusesResponse,
          outstandingMatchupsResponse,
        ] = await Promise.all([
          api.get<GameSummary[]>('/games'),
          api.get<AdminUserSummary[]>('/users/admin'),
          api.get<TeamProphetBonus[]>('/standings/prophet-bonuses'),
          api.get<OutstandingGroupPlayMatchup[]>('/games/outstanding-group-play'),
        ])
        setGames(gamesResponse.data ?? [])
        setUsers(usersResponse.data ?? [])
        setOutstandingMatchups(outstandingMatchupsResponse.data ?? [])
        setTeamSelections(createTeamSelectionMap(usersResponse.data ?? []))
        setGroupPlaySelections(createGroupPlaySelectionMap(usersResponse.data ?? []))
        setProphetBonuses(prophetBonusesResponse.data ?? [])
        setProphetBonusSelections(createProphetBonusSelectionMap(prophetBonusesResponse.data ?? []))
      } catch (error: any) {
        setMessage({
          type: 'error',
          text: error.response?.data?.error || 'Failed to load admin tools.',
        })
      } finally {
        setGamesLoading(false)
        setUsersLoading(false)
      }
    }

    loadAdminData()
  }, [])

  const allSelected = useMemo(
    () => games.length > 0 && selectedGameIds.length === games.length,
    [games.length, selectedGameIds.length]
  )
  const savedProphetBonusByTeam = useMemo(
    () => new Map(prophetBonuses.map(bonus => [bonus.teamName, bonus.bonusPoints])),
    [prophetBonuses]
  )
  const forfeitHomeOptions = useMemo(() => {
    const options = new Map<number, OutstandingGroupPlayMatchup>()
    for (const matchup of outstandingMatchups) {
      if (!options.has(matchup.homeUser.id)) {
        options.set(matchup.homeUser.id, matchup)
      }
    }
    return [...options.values()].sort((a, b) =>
      a.homeUser.displayName.localeCompare(b.homeUser.displayName)
    )
  }, [outstandingMatchups])
  const forfeitAwayOptions = useMemo(() => {
    const homeUserId = Number(forfeitHomeUserId)
    return outstandingMatchups
      .filter(matchup => !homeUserId || matchup.homeUser.id === homeUserId)
      .sort((a, b) => a.awayUser.displayName.localeCompare(b.awayUser.displayName))
  }, [forfeitHomeUserId, outstandingMatchups])
  const selectedForfeitMatchup = useMemo(() => {
    const homeUserId = Number(forfeitHomeUserId)
    const awayUserId = Number(forfeitAwayUserId)
    if (!homeUserId || !awayUserId) {
      return null
    }

    return outstandingMatchups.find(
      matchup => matchup.homeUser.id === homeUserId && matchup.awayUser.id === awayUserId
    ) ?? null
  }, [forfeitAwayUserId, forfeitHomeUserId, outstandingMatchups])

  const toggleGameSelection = (gameId: number) => {
    setSelectedGameIds(current =>
      current.includes(gameId)
        ? current.filter(id => id !== gameId)
        : [...current, gameId]
    )
  }

  const toggleSelectAll = () => {
    setSelectedGameIds(allSelected ? [] : games.map(game => game.id))
  }

  const handleTeamSelectionChange = (userId: number, value: TeamSelectionValue) => {
    setTeamSelections(current => ({
      ...current,
      [userId]: value,
    }))
  }

  const handleGroupPlaySelectionChange = (userId: number, value: GroupPlaySelectionValue) => {
    setGroupPlaySelections(current => ({
      ...current,
      [userId]: value,
    }))
  }

  const handleForfeitHomeChange = (value: string) => {
    setForfeitHomeUserId(value)
    setForfeitAwayUserId('')
  }

  const handleProphetBonusChange = (teamName: LeagueTeamName, value: string) => {
    const numericValue = Math.max(0, Number.parseInt(value || '0', 10) || 0)
    setProphetBonusSelections(current => ({
      ...current,
      [teamName]: numericValue,
    }))
  }

  const handleSaveProphetBonus = async (teamName: LeagueTeamName) => {
    const bonusPoints = prophetBonusSelections[teamName] ?? 0

    try {
      setSavingProphetBonusTeam(teamName)
      setMessage(null)

      const response = await api.patch('/standings/prophet-bonuses', {
        teamName,
        bonusPoints,
      })
      const savedBonus = response.data?.bonus as TeamProphetBonus

      setProphetBonuses(current => {
        const nextBonuses = current.filter(bonus => bonus.teamName !== savedBonus.teamName)
        return [...nextBonuses, savedBonus]
      })
      setProphetBonusSelections(current => ({
        ...current,
        [savedBonus.teamName]: savedBonus.bonusPoints,
      }))
      setMessage({
        type: 'success',
        text: `${teamName} Prophet Bonus updated.`,
      })
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update Prophet Bonus.',
      })
    } finally {
      setSavingProphetBonusTeam(null)
    }
  }

  const handleAssignForfeit = async () => {
    if (!selectedForfeitMatchup) {
      setMessage({ type: 'error', text: 'Select an outstanding Group Play or Playoff matchup.' })
      return
    }

    try {
      setAssigningForfeit(true)
      setMessage(null)

      const response = await api.post('/games/forfeits', {
        homeUserId: selectedForfeitMatchup.homeUser.id,
        awayUserId: selectedForfeitMatchup.awayUser.id,
        outcome: forfeitOutcome,
      })

      const [gamesResponse, outstandingMatchupsResponse] = await Promise.all([
        api.get<GameSummary[]>('/games'),
        api.get<OutstandingGroupPlayMatchup[]>('/games/outstanding-group-play'),
      ])

      setGames(gamesResponse.data ?? [])
      setOutstandingMatchups(outstandingMatchupsResponse.data ?? [])
      setForfeitHomeUserId('')
      setForfeitAwayUserId('')
      setForfeitOutcome('double_loss')
      setMessage({
        type: 'success',
        text: response.data?.message || 'Forfeit assigned successfully.',
      })
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to assign forfeit.',
      })
    } finally {
      setAssigningForfeit(false)
    }
  }

  const handleSaveAssignments = async (user: AdminUserSummary) => {
    const selectedValue = teamSelections[user.id] ?? ''
    const nextLeagueTeamName = selectedValue === '' ? null : selectedValue
    const selectedGroupPlayValue = groupPlaySelections[user.id] ?? ''
    const nextGroupPlaySlot = selectedGroupPlayValue === '' ? null : selectedGroupPlayValue

    if (
      (user.leagueTeamName ?? null) === nextLeagueTeamName &&
      (user.groupPlaySlot ?? null) === nextGroupPlaySlot
    ) {
      return
    }

    try {
      setSavingUserId(user.id)
      setMessage(null)

      const response = await updateUserAssignments(user.id, {
        leagueTeamName: nextLeagueTeamName,
        groupPlaySlot: nextGroupPlaySlot,
      })

      setUsers(current =>
        current.map(entry =>
          entry.id === user.id
            ? {
                ...entry,
                leagueTeamName: response.user.leagueTeamName,
                groupPlaySlot: response.user.groupPlaySlot,
              }
            : entry
        )
      )
      setTeamSelections(current => ({
        ...current,
        [user.id]: (response.user.leagueTeamName ?? '') as TeamSelectionValue,
      }))
      setGroupPlaySelections(current => ({
        ...current,
        [user.id]: (response.user.groupPlaySlot ?? '') as GroupPlaySelectionValue,
      }))
      setMessage({
        type: 'success',
        text: `${user.discordGlobalName ?? user.discordUsername ?? user.username} assignments updated.`,
      })
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update team assignment.',
      })
    } finally {
      setSavingUserId(null)
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedGameIds.length === 0) {
      setMessage({ type: 'error', text: 'Select at least one game to delete.' })
      return
    }

    const confirmed = window.confirm(
      `Delete ${selectedGameIds.length} selected game(s)? This cannot be undone.`
    )
    if (!confirmed) return

    try {
      setDeleting(true)
      setMessage(null)

      const response = await api.delete('/games', {
        data: { gameIds: selectedGameIds },
      })

      const deletedIds: number[] = response.data?.data?.deletedGameIds ?? selectedGameIds
      setGames(current => current.filter(game => !deletedIds.includes(game.id)))
      setSelectedGameIds([])
      setMessage({
        type: 'success',
        text: response.data?.message || 'Games deleted successfully.',
      })
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to delete selected games.',
      })
    } finally {
      setDeleting(false)
    }
  }

  if (authLoading || gamesLoading || usersLoading) {
    return <div className="admin-page">Loading admin tools...</div>
  }

  if (!isAdminUser(currentUser)) {
    return (
      <div className="admin-page">
        <h1>Admin</h1>
        <div className="admin-card">
          <p>You do not have access to this section.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-page">
      <h1>Admin</h1>

      {message && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="admin-card">
        <div className="admin-card-header">
          <div>
            <h2>Competitors</h2>
            <p className="admin-subtitle">
              Discord competitors who have logged in, with team assignment controls and recent login time.
            </p>
            <p className="admin-count">Total competitors: {users.length}</p>
          </div>
        </div>

        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>League Team</th>
                <th>Group Play Slot</th>
                <th>Assign Team</th>
                <th>Assign Group Slot</th>
                <th>Last Login</th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? (
                users.map(user => (
                  <tr key={user.id}>
                    <td>
                      <div className="admin-game-title">
                        <strong>
                          {user.discordGlobalName ?? user.discordUsername ?? user.username}
                        </strong>
                      </div>
                      <div className="admin-game-meta">
                        @{user.discordUsername ?? user.username}
                      </div>
                    </td>
                    <td>{getLeagueTeamDisplayName(user.leagueTeamName)}</td>
                    <td>{user.groupPlaySlot ?? 'Unassigned'}</td>
                    <td>
                      <div className="admin-team-assignment">
                        <select
                          value={teamSelections[user.id] ?? ''}
                          onChange={event =>
                            handleTeamSelectionChange(user.id, event.target.value as TeamSelectionValue)
                          }
                          disabled={savingUserId === user.id}
                          aria-label={`Assign ${user.discordGlobalName ?? user.discordUsername ?? user.username} to a team`}
                        >
                          <option value="">Unassigned</option>
                          {LEAGUE_TEAM_NAMES.map(teamName => (
                            <option key={teamName} value={teamName}>
                              {getLeagueTeamDisplayName(teamName)}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="btn btn-secondary admin-team-save"
                          onClick={() => handleSaveAssignments(user)}
                          disabled={
                            savingUserId === user.id ||
                            ((teamSelections[user.id] ?? '') === (user.leagueTeamName ?? '') &&
                              (groupPlaySelections[user.id] ?? '') === (user.groupPlaySlot ?? ''))
                          }
                        >
                          {savingUserId === user.id ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </td>
                    <td>
                      <select
                        value={groupPlaySelections[user.id] ?? ''}
                        onChange={event =>
                          handleGroupPlaySelectionChange(user.id, event.target.value as GroupPlaySelectionValue)
                        }
                        disabled={savingUserId === user.id}
                        aria-label={`Assign ${user.discordGlobalName ?? user.discordUsername ?? user.username} to a group play slot`}
                      >
                        <option value="">Unassigned</option>
                        {GROUP_PLAY_SLOTS.map(slot => (
                          <option key={slot} value={slot}>
                            {slot}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>{formatDate(user.lastLoginAt ?? user.createdAt)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6}>No Discord competitors found yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <div>
            <h2>Prophet Bonuses</h2>
            <p className="admin-subtitle">
              Admin-assigned Crystal Balls added to each team&apos;s standings total.
            </p>
          </div>
        </div>

        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Team</th>
                <th>Current Prophet Bonus</th>
                <th>Assign Prophet Bonus</th>
              </tr>
            </thead>
            <tbody>
              {LEAGUE_TEAM_NAMES.map(teamName => {
                const savedValue = savedProphetBonusByTeam.get(teamName) ?? 0
                const selectedValue = prophetBonusSelections[teamName] ?? 0

                return (
                  <tr key={teamName}>
                    <td>{teamName}</td>
                    <td>{savedValue}</td>
                    <td>
                      <div className="admin-team-assignment">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={selectedValue}
                          onChange={event => handleProphetBonusChange(teamName, event.target.value)}
                          disabled={savingProphetBonusTeam === teamName}
                          aria-label={`Assign Prophet Bonus to ${teamName}`}
                        />
                        <button
                          type="button"
                          className="btn btn-secondary admin-team-save"
                          onClick={() => handleSaveProphetBonus(teamName)}
                          disabled={
                            savingProphetBonusTeam === teamName ||
                            selectedValue === savedValue
                          }
                        >
                          {savingProphetBonusTeam === teamName ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <div>
            <h2>Assign Forfeit</h2>
            <p className="admin-subtitle">
              Select an outstanding Group Play or Playoff matchup and assign either a single
              forfeit win/loss or losses to both owners.
            </p>
            <p className="admin-count">Outstanding matchups: {outstandingMatchups.length}</p>
          </div>
        </div>

        <div className="admin-forfeit-grid">
          <label className="admin-field">
            <span>Scheduled Home Owner</span>
            <select
              value={forfeitHomeUserId}
              onChange={event => handleForfeitHomeChange(event.target.value)}
              disabled={assigningForfeit || outstandingMatchups.length === 0}
            >
              <option value="">Select home owner</option>
              {forfeitHomeOptions.map(matchup => (
                <option key={`${matchup.homeSlot}-${matchup.homeUser.id}`} value={matchup.homeUser.id}>
                  {matchup.homeUser.displayName} ({matchup.homeSlot})
                </option>
              ))}
            </select>
          </label>

          <label className="admin-field">
            <span>Scheduled Away Owner</span>
            <select
              value={forfeitAwayUserId}
              onChange={event => setForfeitAwayUserId(event.target.value)}
              disabled={assigningForfeit || forfeitAwayOptions.length === 0}
            >
              <option value="">Select away owner</option>
              {forfeitAwayOptions.map(matchup => (
                <option
                  key={`${matchup.homeSlot}-${matchup.awaySlot}-${matchup.awayUser.id}`}
                  value={matchup.awayUser.id}
                >
                  {matchup.awayUser.displayName} ({matchup.awaySlot})
                </option>
              ))}
            </select>
          </label>

          <label className="admin-field">
            <span>Forfeit Result</span>
            <select
              value={forfeitOutcome}
              onChange={event => setForfeitOutcome(event.target.value as ForfeitOutcome)}
              disabled={assigningForfeit || !selectedForfeitMatchup}
            >
              <option value="double_loss">Both owners receive forfeit losses</option>
              <option value="home_win">Home owner receives forfeit win</option>
              <option value="away_win">Away owner receives forfeit win</option>
            </select>
          </label>

          <button
            type="button"
            className="btn btn-secondary admin-forfeit-save"
            onClick={handleAssignForfeit}
            disabled={assigningForfeit || !selectedForfeitMatchup}
          >
            {assigningForfeit ? 'Assigning...' : 'Assign Forfeit'}
          </button>
        </div>

        {selectedForfeitMatchup ? (
          <p className="admin-forfeit-preview">
            {selectedForfeitMatchup.homeUser.displayName} ({selectedForfeitMatchup.homeSlot}) vs{' '}
            {selectedForfeitMatchup.awayUser.displayName} ({selectedForfeitMatchup.awaySlot})
          </p>
        ) : null}
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <div>
            <h2>Manage Games</h2>
            <p className="admin-subtitle">
              Select games below to permanently remove them from the database.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleDeleteSelected}
            disabled={deleting || selectedGameIds.length === 0}
          >
            {deleting ? 'Deleting...' : `Delete Selected (${selectedGameIds.length})`}
          </button>
        </div>

        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all games"
                  />
                </th>
                <th>Game</th>
                <th>Coaches</th>
                <th>Played</th>
              </tr>
            </thead>
            <tbody>
              {games.length > 0 ? (
                games.map(game => (
                  <tr key={game.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedGameIds.includes(game.id)}
                        onChange={() => toggleGameSelection(game.id)}
                        aria-label={`Select game ${game.id}`}
                      />
                    </td>
                    <td>
                      <div className="admin-game-title">
                        <strong>{game.homeTeam}</strong> {game.homeScore} vs {game.awayScore}{' '}
                        <strong>{game.awayTeam}</strong>
                      </div>
                      <div className="admin-game-meta">Game ID {game.id}</div>
                    </td>
                    <td>
                      <div className="admin-game-meta">
                        {game.player1User?.displayName ?? 'Unassigned'} vs{' '}
                        {game.player2User?.displayName ?? 'Unassigned'}
                      </div>
                    </td>
                    <td>{formatDate(game.playedAt ?? game.uploadedAt)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4}>No games found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
